import { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categoryInfo, categoryArticles } from '@/mocks/categories';
import { sportsCategories } from '@/mocks/home';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [activeCategory, setActiveCategory] = useState(slug || 'football');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [location.pathname]);

  const info = categoryInfo[activeCategory] || categoryInfo.football;
  const articles = categoryArticles[activeCategory] || [];

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `bb体育 - ${info.name}赛事资讯`,
    description: `bb体育${info.name}频道 - ${info.description}。获取最新${info.name}赛事资讯、赛程、比分及专业分析。`,
    url: `https://www.bbsports.com/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: 'bb体育',
      url: 'https://www.bbsports.com',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.bbsports.com' },
      { '@type': 'ListItem', position: 2, name: info.name, item: `https://www.bbsports.com/category/${activeCategory}` },
    ],
  };

  return (
    <>
      <SEOHead
        title={`bb体育 - ${info.name}赛事资讯 | 最新${info.name}赛事分析与直播`}
        description={`bb体育${info.name}频道 - ${info.description}。获取最新${info.name}赛事资讯、赛程、比分及专业分析。加入bb体育，感受全方位${info.name}体育世界。`}
        keywords={`bb体育,${info.name},体育赛事,赛事资讯,体育直播,bb体育官网,bb体育平台`}
        canonicalUrl={`https://www.bbsports.com/category/${activeCategory}`}
        schema={{ ...categorySchema, ...breadcrumbSchema }}
      />

      <Navbar />

      <main className="pt-20 md:pt-24">
        <div className="container-custom">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: info.name },
            ]}
          />
        </div>

        <section className="bg-background-900 relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 right-0 w-80 h-80 bg-primary-500 rounded-full blur-3xl translate-x-1/4 -translate-y-1/4"></div>
          </div>
          <div className="container-custom relative z-10 py-12 md:py-16">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 md:w-14 md:h-14 bg-primary-500/20 rounded-xl flex items-center justify-center">
                <i className={`${info.icon} text-primary-400 text-2xl`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50">
                  {info.name}赛事
                </h1>
                <p className="text-background-400 text-sm mt-1">{info.description}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100">
          <div className="container-custom py-4 md:py-6">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
              {sportsCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeCategory === cat.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 border border-background-200 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="container-custom">
            {articles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {articles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-44 md:h-48 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        title={article.title}
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xs font-medium text-primary-600 bg-primary-100 px-2.5 py-0.5 rounded-full whitespace-nowrap">
                          {article.category}
                        </span>
                        <span className="text-xs text-foreground-400 whitespace-nowrap">{article.date}</span>
                      </div>
                      <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-foreground-500 text-xs leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-background-100">
                        <span className="text-xs text-foreground-400">{article.author}</span>
                        <span className="text-xs text-foreground-300">·</span>
                        <span className="text-xs text-foreground-400 flex items-center gap-1">
                          <i className="ri-eye-line text-xs"></i> {article.views.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 bg-background-100 rounded-full flex items-center justify-center">
                  <i className={`${info.icon} text-foreground-300 text-2xl`}></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-600 mb-2">暂无相关资讯</h3>
                <p className="text-sm text-foreground-400">该分类下暂无文章，我们会尽快更新相关内容</p>
              </div>
            )}
          </div>
        </section>

        {articles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="container-custom text-center">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-4">
                浏览更多体育分类
              </h2>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                {sportsCategories.filter(c => c.id !== activeCategory).slice(0, 4).map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.id}`}
                    className="px-5 py-2.5 rounded-lg border border-background-300 text-foreground-600 text-sm hover:border-primary-300 hover:text-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    <i className={`${cat.icon} mr-1.5`}></i>查看{cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}